import { Link, Outlet, useLocation } from 'react-router-dom'
import {
  BookOpen,
  Calculator,
  ChevronDown,
  FlaskConical,
  GraduationCap,
  Leaf,
  Menu,
  Users,
  X,
  Home as HomeIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { GarlandBand } from './ornaments'
import MailboxButton from './MailboxButton'

// 桌面专用构建:裁剪导航(去掉沙龙/下载桌面版/返回主站,新增调香计算器)
const DESKTOP = import.meta.env.VITE_DESKTOP === '1'

const navigation = [
  { name: '首页', href: '/', icon: FlaskConical },
  { name: '原料', href: '/ingredients', icon: Leaf },
  { name: '配方', href: '/formulas', icon: FlaskConical },
  { name: '香水', href: '/perfumes', icon: BookOpen },
  ...(DESKTOP
    ? [{ name: '计算器', href: '/calculator', icon: Calculator }]
    : [{ name: '沙龙调香台', href: '/salon', icon: Users }]),
]

const moreLinks = DESKTOP
  ? [{ name: '关于', href: '/about' }]
  : [
      { name: '关于', href: '/about' },
      { name: '下载桌面版', href: '/download' },
    ]

export default function Layout() {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setMoreOpen(false)
      }
    }

    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }

    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  return (
    <div className="min-h-screen text-[#2b241d]">
      <header className="sticky top-0 z-40 border-b border-[rgba(106,80,52,0.14)] bg-[rgba(247,242,234,0.9)] backdrop-blur-xl">
        {/* 桌面版:顶部拖拽区(去系统标题栏后,对应原生窗口控件覆盖),仅桌面构建渲染。
            不带品牌文案——用户明确要求去掉顶部"香研社·Perfume Lab"文字,保持整体感 */}
        {DESKTOP && <div className="desktop-titlebar" aria-hidden="true" />}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 py-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(139,46,31,0.18)] bg-[rgba(139,46,31,0.08)] text-[#8b2e1f]">
                <FlaskConical className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xl font-semibold text-[#2b241d]">香研社</p>
                <p className="text-xs text-[#756a5d]">Perfume Lab</p>
              </div>
            </Link>

            <nav className="hidden items-center gap-1 lg:flex">
              {navigation.map((item) => {
                const Icon = item.icon
                const active = isActive(item.href)

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm transition-all ${
                      active
                        ? 'bg-[rgba(139,46,31,0.1)] text-[#8b2e1f]'
                        : 'text-[#5d544a] hover:bg-[rgba(139,46,31,0.05)] hover:text-[#8b2e1f]'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                )
              })}

              <div ref={moreRef} className="relative">
                <button
                  type="button"
                  onClick={() => setMoreOpen((value) => !value)}
                  className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm text-[#5d544a] transition-all hover:bg-[rgba(139,46,31,0.05)] hover:text-[#8b2e1f]"
                >
                  更多
                  <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
                </button>

                {moreOpen && (
                  <div className="absolute right-0 top-full mt-2 w-44 rounded-xl border border-[rgba(106,80,52,0.14)] bg-white py-2 shadow-lg">
                    {moreLinks.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMoreOpen(false)}
                        className="block px-4 py-2 text-sm text-[#5d544a] hover:bg-[rgba(139,46,31,0.05)] hover:text-[#8b2e1f]"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            <div className="hidden items-center gap-2 lg:flex">
              {/* 返回主站:香水站挂在 /perfume/ 子路径,站点根即门户首页。桌面端无站点根,不显示 */}
              {!DESKTOP && (
                <a
                  href="/"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(106,80,52,0.18)] bg-[rgba(255,251,245,0.72)] px-4 py-2 text-sm font-medium text-[#5d544a] transition-colors hover:border-[rgba(139,46,31,0.3)] hover:bg-[rgba(255,246,236,0.96)] hover:text-[#8b2e1f]"
                >
                  <HomeIcon className="h-4 w-4" />
                  返回主站
                </a>
              )}
              <Link to="/teaching" className="btn-primary">
                <GraduationCap className="h-4 w-4" />
                调香专业课程
              </Link>
            </div>

            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(106,80,52,0.18)] bg-[rgba(255,251,245,0.72)] text-[#5d544a] lg:hidden"
              onClick={() => setMobileOpen((value) => !value)}
              aria-label="切换导航"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {mobileOpen && (
            <div className="paper-panel mb-4 space-y-3 p-4 lg:hidden">
              <div className="grid gap-2">
                {navigation.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.href)

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm ${
                        active
                          ? 'bg-[rgba(139,46,31,0.1)] text-[#8b2e1f]'
                          : 'bg-[rgba(255,251,245,0.78)] text-[#5d544a]'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.name}
                    </Link>
                  )
                })}
              </div>

              <div className="paper-rule" />

              <div className="grid gap-1">
                {moreLinks.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="rounded-xl px-4 py-2.5 text-sm text-[#6c6257] hover:bg-[rgba(139,46,31,0.05)]"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              <Link to="/teaching" className="btn-primary w-full" onClick={() => setMobileOpen(false)}>
                <GraduationCap className="h-4 w-4" />
                调香专业课程
              </Link>

              {!DESKTOP && (
                <a
                  href="/"
                  className="flex items-center justify-center gap-2 rounded-full border border-[rgba(106,80,52,0.18)] bg-[rgba(255,251,245,0.72)] px-4 py-2.5 text-sm font-medium text-[#5d544a]"
                  onClick={() => setMobileOpen(false)}
                >
                  <HomeIcon className="h-4 w-4" />
                  返回主站
                </a>
              )}
            </div>
          )}
        </div>
      </header>

      <GarlandBand className="block h-6 w-full opacity-90" />

      <main className="mx-auto max-w-7xl px-4 pb-16 pt-8 sm:px-6 lg:px-8">
        <Outlet />
      </main>

      {/* 网页版右下角悬浮:意见信箱(桌面 EXE 不渲染) */}
      <MailboxButton />

      <footer className="border-t border-[rgba(106,80,52,0.14)] bg-[rgba(248,242,233,0.82)]">
        <GarlandBand flip className="block h-6 w-full opacity-75" />
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-[#2b241d]">香研社</h2>
              <p className="mt-1 text-sm text-[#6a6056]">专业调香配方与香气素材库</p>
            </div>

            <div className="grid grid-flow-col gap-x-6 gap-y-2 text-sm text-[#6a6056]">
              <Link to="/teaching" className="hover:text-[#8b2e1f]">
                调香专业课程
              </Link>
              <Link to="/about" className="hover:text-[#8b2e1f]">
                关于
              </Link>
              {!DESKTOP && (
                <Link to="/download" className="hover:text-[#8b2e1f]">
                  下载桌面版
                </Link>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
